import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaInfoCircle, FaMap, FaRocket, FaChartLine, FaUsers, FaLightbulb, FaCog, FaGraduationCap, FaPlus, FaEdit, FaTrash, FaSave, FaEye, FaArrowLeft, FaStar, FaSearch, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, setDoc, deleteDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { isAlaCarteItemHiddenFromPurchase } from '../../utils/programPricing';
import { isRetiredProgram } from '../../utils/retiredPrograms';
import PortalBackground from '../../components/PortalBackground';
import ProgramDetail, { CUSTOM_PROGRAM, CUSTOM_TIER } from '../ProgramDetail';

const DEFAULT_MINIMUM_TERM = 12;

/**
 * Minimum term is stored as a whole number of months (1-60). Blank/garbage falls
 * back to the default — coercing while the user is still typing is what made the
 * field snap back mid-edit.
 */
const normalizeMinimumTerm = (value) => {
  const months = parseInt(String(value ?? '').trim(), 10);
  if (!Number.isFinite(months)) return DEFAULT_MINIMUM_TERM;
  return Math.min(60, Math.max(1, months));
};

const programsData = {
  'Lite': {
    name: 'Lite',
    description: 'Essential support for getting your business started',
    pricePerMonth: 299,
    isMostPopular: true,
    minimumTerm: 12,
    showcaseFeatures: [
      { text: 'Business Strategy Review', showcase: true, description: '' },
      { text: 'Financial Projections', showcase: true, description: '' }
    ],
    features: {
      'Business Strategy Review': { quantity: '1', showcase: true },
      'Financial Projections': { quantity: '1', showcase: true },
      'Monthly Check-ins': { quantity: '12', showcase: false },
      'Resource Library': { quantity: '1', showcase: false }
    }
  },
  'Plus': {
    name: 'Plus',
    description: 'Enhanced support for growing businesses with strategic guidance',
    pricePerMonth: 449,
    isMostPopular: false,
    minimumTerm: 12,
    showcaseFeatures: [
      { text: 'Business Strategy Review', showcase: true, description: '' },
      { text: 'Operational Assessment', showcase: true, description: '' },
      { text: 'Growth Planning', showcase: true, description: '' }
    ],
    features: {
      'Business Strategy Review': { quantity: '1', showcase: true },
      'Operational Assessment': { quantity: '1', showcase: true },
      'Growth Planning': { quantity: '1', showcase: true },
      'Monthly Check-ins': { quantity: '12', showcase: false },
      'Performance Metrics': { quantity: '1', showcase: false },
      'Priority Support': { quantity: '1', showcase: false },
      'Resource Library': { quantity: '1', showcase: false }
    }
  },
  'Pro': {
    name: 'Pro',
    description: 'Professional-grade support and strategic guidance for scaling businesses',
    pricePerMonth: 549,
    isMostPopular: false,
    minimumTerm: 12,
    showcaseFeatures: [
      { text: 'Business Strategy Review', showcase: true, description: '' },
      { text: 'Operational Assessment', showcase: true, description: '' },
      { text: 'Growth Planning', showcase: true, description: '' },
      { text: 'Executive Coaching', showcase: true, description: '' }
    ],
    features: {
      'Business Strategy Review': { quantity: '1', showcase: true },
      'Operational Assessment': { quantity: '1', showcase: true },
      'Growth Planning': { quantity: '1', showcase: true },
      'Executive Coaching': { quantity: '1', showcase: true },
      'Monthly Check-ins': { quantity: '12', showcase: false },
      'Priority Support': { quantity: '1', showcase: false },
      'Quarterly Reviews': { quantity: '4', showcase: false },
      'Resource Library': { quantity: '1', showcase: false }
    }
  }
};

const ProgramsTemplate = () => {
  const navigate = useNavigate();
  const [selectedProgram, setSelectedProgram] = useState('Lite');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState({
    monthly: { name: 'Monthly', discount: 0, months: 1 },
    annual: { name: 'Annual', discount: 20, months: 12 }
  });
  const [programs, setPrograms] = useState(programsData);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showHourlyOptionsModal, setShowHourlyOptionsModal] = useState(false);
  const [hourlyOptions, setHourlyOptions] = useState([]);
  const [showFeatureSelectionModal, setShowFeatureSelectionModal] = useState(false);
  const [currentProgramForFeature, setCurrentProgramForFeature] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureSearchQuery, setFeatureSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [programSelectedItems, setProgramSelectedItems] = useState({});
  const [programContractItems, setProgramContractItems] = useState({});

  const [itemOrder, setItemOrder] = useState({});
  const [isOrderModified, setIsOrderModified] = useState(false);
  const [isValuesModified, setIsValuesModified] = useState(false);
  const [showcaseOrder, setShowcaseOrder] = useState({});
  const [isShowcaseOrderModified, setIsShowcaseOrderModified] = useState(false);

  const [notification, setNotification] = useState(null);

  // Notification function
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // New program form state
  const [newProgram, setNewProgram] = useState({
    name: '',
    description: '',
    pricePerMonth: 0,
    isMostPopular: false,
    minimumTerm: 12,
    features: {}
  });

  // Load data from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load programs data
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach(doc => {
            const programData = doc.data();
            // Ensure the program has the required structure
            if (programData && typeof programData === 'object') {
              // Ensure features property exists and is an object
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              // Ensure showcaseFeatures exists - migrate from features with showcase:true if needed
              if (!Array.isArray(programData.showcaseFeatures)) {
                programData.showcaseFeatures = Object.entries(programData.features || {})
                  .filter(([_, fd]) => fd?.showcase)
                  .map(([name]) => ({ text: name, showcase: true }));
              }
              // Normalize: convert legacy string format to {text, showcase, description}
              programData.showcaseFeatures = (programData.showcaseFeatures || []).map(item =>
                typeof item === 'string' ? { text: item, showcase: true, description: '' } : { ...item, description: item.description || '' }
              );
              // Ensure other required properties exist
              if (!programData.name) programData.name = doc.id;
              if (!programData.description) programData.description = 'Program description';
              // A save writes these back, so recover a stored price/term before defaulting:
              // readers fall back to monthlyPrice/price, and a "6" string must stay 6, not 12.
              if (!programData.pricePerMonth) {
                programData.pricePerMonth = Number(programData.monthlyPrice ?? programData.price) || 0;
              }
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              if (typeof programData.minimumTerm !== 'number') {
                const term = parseInt(programData.minimumTerm, 10);
                programData.minimumTerm = Number.isFinite(term) && term > 0 ? term : 12;
              }
              
              programsData[doc.id] = programData;
            }
          });
          
          // The defaults above (minimumTerm, pricePerMonth, ...) only fill the on-screen copy.
          // Loading never writes back: every programs doc, including retired and legacy ones,
          // is kept exactly as stored. Lite/Plus/Pro carry live clients' deliverables and
          // implementation-hour allowances, and their prices are what those clients signed.

          console.log('Loaded programs:', Object.keys(programsData));
          setPrograms(programsData);
          
          // Set first program as selected if none selected
          if (!selectedProgram || !programsData[selectedProgram]) {
            setSelectedProgram(Object.keys(programsData)[0] || '');
          }
        } else {
          // If no programs in database, save default programs
          await saveDefaultPrograms();
          setPrograms(programsData); // Use the default programs data
          setSelectedProgram('Lite');
        }

        // Load billing cycles data
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach(doc => {
            billingData[doc.id] = doc.data();
          });
          setBillingCycles(billingData);
        } else {
          // If no billing cycles in database, save default billing cycles
          await saveDefaultBillingCycles();
          setBillingCycles({
            monthly: { name: 'Monthly', discount: 0, months: 1 },
            quarterly: { name: 'Quarterly', discount: 10, months: 3 },
            annual: { name: 'Annual', discount: 20, months: 12 }
          });
        }

        // Load available items from alaCarteItems collection
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableItems(itemsData);

        // Load item order
        await loadItemOrder();
        
        // Load showcase order
        await loadShowcaseOrder();
        
        // Load minimum term
        
        // Load hourly options
        await loadHourlyOptions();
        
        // Load contract items for all programs
        await loadAllContractItems();
        
      } catch (error) {
        console.error('Error loading data:', error);
        // Set default data on error with validation
        const validatedPrograms = {};
        Object.entries(programsData).forEach(([key, program]) => {
          validatedPrograms[key] = {
            ...program,
            features: program.features || {},
            showcaseFeatures: Array.isArray(program.showcaseFeatures) ? program.showcaseFeatures : [],
            name: program.name || key,
            description: program.description || 'Program description',
            pricePerMonth: program.pricePerMonth || 0,
            isMostPopular: program.isMostPopular || false,
            minimumTerm: typeof program.minimumTerm === 'number' ? program.minimumTerm : 12
          };
        });
        setPrograms(validatedPrograms);
        setBillingCycles({
          monthly: { name: 'Monthly', discount: 0, months: 1 },
          annual: { name: 'Annual', discount: 20, months: 12 }
        });
        setSelectedProgram('Lite');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save default programs to Firestore
  const saveDefaultPrograms = async () => {
    try {
      const batch = [];
      Object.entries(programsData).forEach(([key, program]) => {
        const docRef = doc(db, 'programs', key);
        batch.push(setDoc(docRef, program));
      });
      await Promise.all(batch);
      console.log('Default programs saved to Firestore');
    } catch (error) {
      console.error('Error saving default programs:', error);
    }
  };

  // Save default billing cycles to Firestore
  const saveDefaultBillingCycles = async () => {
    try {
      const batch = [];
      Object.entries({
        monthly: { 
          name: 'Monthly', 
          discount: 0, 
          months: 1,
          description: 'Standard monthly billing cycle',
          isActive: true
        },
        annual: { 
          name: 'Annual', 
          discount: 20, 
          months: 12,
          description: 'Annual billing with 20% discount',
          isActive: true
        }
      }).forEach(([key, cycle]) => {
        const docRef = doc(db, 'billingCycles', key);
        const cycleWithMetadata = {
          ...cycle,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        batch.push(setDoc(docRef, cycleWithMetadata));
      });
      await Promise.all(batch);
      console.log('Default billing cycles saved to Firestore');
    } catch (error) {
      console.error('Error saving default billing cycles:', error);
      throw error; // Re-throw to handle in UI
    }
  };

  // Save programs to Firestore
  const saveProgramsToFirestore = async (updatedPrograms) => {
    try {
      const batch = [];
      Object.entries(updatedPrograms).forEach(([key, program]) => {
        const docRef = doc(db, 'programs', key);
        // mergeFields: replaces only the fields this page holds (so a removed feature really
        // goes), while fields it doesn't edit (status flags, history) survive the save.
        batch.push(setDoc(docRef, program, { mergeFields: Object.keys(program) }));
      });
      await Promise.all(batch);
      console.log('Programs saved to Firestore');
    } catch (error) {
      console.error('Error saving programs to Firestore:', error);
      // Re-throw so callers report the failure instead of showing "saved successfully!"
      // on a write that never landed.
      throw error;
    }
  };

  // Save billing cycles to Firestore
  const saveBillingCyclesToFirestore = async (updatedBillingCycles) => {
    try {
      const batch = [];
      Object.entries(updatedBillingCycles).forEach(([key, cycle]) => {
        const docRef = doc(db, 'billingCycles', key);
        // Add metadata to each billing cycle
        const cycleWithMetadata = {
          ...cycle,
          updatedAt: new Date().toISOString(),
          isActive: cycle.isActive !== undefined ? cycle.isActive : true
        };
        batch.push(setDoc(docRef, cycleWithMetadata));
      });
      await Promise.all(batch);
      console.log('Billing cycles saved to Firestore');
    } catch (error) {
      console.error('Error saving billing cycles to Firestore:', error);
      throw error; // Re-throw to handle in UI
    }
  };

  // Delete billing cycle from Firestore
  const deleteBillingCycleFromFirestore = async (cycleKey) => {
    try {
      await deleteDoc(doc(db, 'billingCycles', cycleKey));
      console.log('Billing cycle deleted from Firestore');
    } catch (error) {
      console.error('Error deleting billing cycle from Firestore:', error);
      throw error;
    }
  };

  // Filter available items based on search and department
  const filteredAvailableItems = availableItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(featureSearchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(featureSearchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All' || item.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  // Get department color for styling
  const getDepartmentColor = (department) => {
    const colors = {
      'General': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'Sales': 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200',
      'Operations': 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200',
      'Finance': 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200',
      'Technology': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-200',
      'Marketing': 'bg-pink-100 text-pink-800 dark:bg-pink-700 dark:text-pink-200',
      'HR': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200'
    };
    return colors[department] || colors['General'];
  };

  // Validate program data to ensure it has required structure
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return {
        name: 'Unknown Program',
        description: 'Program description',
        pricePerMonth: 0,
        isMostPopular: false,
        minimumTerm: 12,
        features: {},
        showcaseFeatures: []
      };
    }
    
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      minimumTerm: typeof program.minimumTerm === 'number' ? program.minimumTerm : 12,
      features: program.features && typeof program.features === 'object' ? program.features : {},
      showcaseFeatures: Array.isArray(program.showcaseFeatures) ? program.showcaseFeatures : []
    };
  };



  // Function to determine if an item should be checkbox or quantity
  const isCheckboxItem = (item) => {
    // You can customize this logic based on your needs
    // For now, let's make it based on the item name or department
    return item.quantity === '0' || item.quantity === 0;
  };

  // Function to handle item value change
  const handleItemValueChange = async (programKey, itemName, value, isCheckbox) => {
    if (isCheckbox) {
      // For checkbox items, value is boolean
      if (value) {
        await addFeature(programKey, itemName, '0', false); // '0' indicates checkbox mode
      } else {
        removeFeature(programKey, itemName);
      }
    } else {
      // For quantity items, value is the quantity string
      if (value === '0' || value === '') {
        removeFeature(programKey, itemName);
      } else {
        const isShowcase = programs[programKey]?.features[itemName]?.showcase || false;
        await addFeature(programKey, itemName, value, isShowcase);
      }
    }
    setIsValuesModified(true);
  };

  // Item ordering functions
  const moveItem = (department, itemId, direction) => {
    setItemOrder(prev => {
      const deptItems = prev[department] || [];
      const index = deptItems.findIndex(item => item.id === itemId);
      if (index === -1) return prev;
      
      const newOrder = [...deptItems];
      if (direction === 'up' && index > 0) {
        [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      } else if (direction === 'down' && index < newOrder.length - 1) {
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      }
      
      setIsOrderModified(true);
      return {
        ...prev,
        [department]: newOrder
      };
    });
  };

  const saveItemOrder = async () => {
    try {
      // Save the item order to Firestore
      await setDoc(doc(db, 'settings', 'itemOrder'), {
        order: itemOrder,
        updatedAt: new Date().toISOString()
      });
      
      setIsOrderModified(false);
      console.log('Item order saved successfully');
    } catch (error) {
      console.error('Error saving item order:', error);
    }
  };

    const saveQuantities = async () => {
    try {
      // Save the updated programs to Firestore
      await saveProgramsToFirestore(programs);
      
      setIsValuesModified(false);
      console.log('Values saved successfully');
    } catch (error) {
      console.error('Error saving values:', error);
    }
  };

  // Showcase feature ordering functions
  const moveShowcaseFeature = (programKey, featureName, direction) => {
    setShowcaseOrder(prev => {
      const programOrder = prev[programKey] || [];
      const index = programOrder.findIndex(feature => feature === featureName);
      if (index === -1) return prev;
      
      const newOrder = [...programOrder];
      if (direction === 'up' && index > 0) {
        [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      } else if (direction === 'down' && index < newOrder.length - 1) {
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      }
      
      setIsShowcaseOrderModified(true);
      return {
        ...prev,
        [programKey]: newOrder
      };
    });
  };

  const saveShowcaseOrder = async () => {
    try {
      // Save the showcase order to Firestore
      await setDoc(doc(db, 'settings', 'showcaseOrder'), {
        order: showcaseOrder,
        updatedAt: new Date().toISOString()
      });
      
      setIsShowcaseOrderModified(false);
      console.log('Showcase order saved successfully');
    } catch (error) {
      console.error('Error saving showcase order:', error);
    }
  };

  const getOrderedShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.features) return [];
    
    const showcaseFeatures = Object.entries(program.features)
      .filter(([_, featureData]) => featureData.showcase)
      .map(([featureName, _]) => featureName);
    
    const savedOrder = showcaseOrder[programKey] || [];
    
    if (savedOrder.length === 0 && showcaseFeatures.length > 0) {
      // If no saved order, use default order and save it
      setShowcaseOrder(prev => ({
        ...prev,
        [programKey]: showcaseFeatures
      }));
      return showcaseFeatures;
    }
    
    // Sort features according to saved order
    const orderedFeatures = [];
    savedOrder.forEach(featureName => {
      if (showcaseFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    // Add any new showcase features that weren't in the saved order
    showcaseFeatures.forEach(featureName => {
      if (!orderedFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    // If there are new features, update the showcaseOrder state
    if (orderedFeatures.length !== savedOrder.length) {
      setShowcaseOrder(prev => ({
        ...prev,
        [programKey]: orderedFeatures
      }));
    }
    
    return orderedFeatures;
  };





  const loadItemOrder = async () => {
    try {
      const orderDoc = await getDocs(collection(db, 'settings'));
      const orderData = orderDoc.docs.find(doc => doc.id === 'itemOrder');
      if (orderData) {
        setItemOrder(orderData.data().order || {});
      } else {
        // Initialize default order for all departments
        const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
        const defaultOrder = {};
        departments.forEach(dept => {
          const deptItems = availableItems.filter(item => item.department === dept);
          defaultOrder[dept] = deptItems.map(item => ({ id: item.id, name: item.name }));
        });
        setItemOrder(defaultOrder);
      }
    } catch (error) {
      console.error('Error loading item order:', error);
      // Set default order on error
      const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
      const defaultOrder = {};
      departments.forEach(dept => {
        const deptItems = availableItems.filter(item => item.department === dept);
        defaultOrder[dept] = deptItems.map(item => ({ id: item.id, name: item.name }));
      });
      setItemOrder(defaultOrder);
    }
  };

  const loadShowcaseOrder = async () => {
    try {
      const orderDoc = await getDocs(collection(db, 'settings'));
      const showcaseOrderData = orderDoc.docs.find(doc => doc.id === 'showcaseOrder');
      if (showcaseOrderData) {
        setShowcaseOrder(showcaseOrderData.data().order || {});
      }
    } catch (error) {
      console.error('Error loading showcase order:', error);
    }
  };

  const loadHourlyOptions = async () => {
    try {
      const settingsDoc = await getDocs(collection(db, 'settings'));
      const hourlyOptionsData = settingsDoc.docs.find(doc => doc.id === 'hourlyOptions');
      if (hourlyOptionsData) {
        setHourlyOptions(hourlyOptionsData.data().options || []);
      } else {
        // Initialize with default hourly options if none exist
        const defaultOptions = [
          {
            id: 'hourly',
            hours: 'unlimited',
            pricePerMonth: 150,
            name: 'Hourly Rate'
          },
          {
            id: '5-hours',
            hours: 5,
            pricePerMonth: 500,
            name: '5 Hours/Month'
          },
          {
            id: '10-hours',
            hours: 10,
            pricePerMonth: 800,
            name: '10 Hours/Month'
          }
        ];
        setHourlyOptions(defaultOptions);
        // Save default options to Firestore
        await saveHourlyOptionsToFirestore(defaultOptions);
      }
    } catch (error) {
      console.error('Error loading hourly options:', error);
      // Set default on error
      const defaultOptions = [
        {
          id: 'hourly',
          hours: 'unlimited',
          pricePerMonth: 150,
          name: 'Hourly Rate'
        },
        {
          id: '5-hours',
          hours: 5,
          pricePerMonth: 500,
          name: '5 Hours/Month'
        },
        {
          id: '10-hours',
          hours: 10,
          pricePerMonth: 800,
          name: '10 Hours/Month'
        }
      ];
      setHourlyOptions(defaultOptions);
    }
  };

  const saveHourlyOptionsToFirestore = async (options) => {
    try {
      await setDoc(doc(db, 'settings', 'hourlyOptions'), {
        options: options,
        updatedAt: new Date().toISOString()
      });
      console.log('Hourly options saved to Firestore');
    } catch (error) {
      console.error('Error saving hourly options to Firestore:', error);
      throw error;
    }
  };

  // Save contract items to subcollection. One atomic batch, so a failed save can't leave a
  // program with its old items deleted and the new ones missing.
  const saveContractItemsToFirestore = async (programKey, contractItems) => {
    try {
      const batch = writeBatch(db);
      const programRef = doc(db, 'programs', programKey);
      
      // Clear existing contract items
      const existingItems = await getDocs(collection(db, 'programs', programKey, 'contractItems'));
      existingItems.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Add new contract items
      contractItems.forEach((item, index) => {
        if (item.isIncluded) {
          const contractItemRef = doc(collection(programRef, 'contractItems'));
          batch.set(contractItemRef, {
            ...item,
            order: index,
            addedAt: new Date().toISOString()
          });
        }
      });
      
      await batch.commit();
      console.log('Contract items saved to subcollection for program:', programKey);
    } catch (error) {
      console.error('Error saving contract items to subcollection:', error);
      throw error;
    }
  };

  // Load contract items from subcollection
  const loadContractItemsFromFirestore = async (programKey) => {
    try {
      const contractItemsSnapshot = await getDocs(collection(db, 'programs', programKey, 'contractItems'));
      const contractItems = contractItemsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      return contractItems;
    } catch (error) {
      console.error('Error loading contract items from subcollection:', error);
      return [];
    }
  };

  // Load contract items for all programs
  const loadAllContractItems = async () => {
    try {
      const contractItemsData = {};
      for (const programKey of Object.keys(programs)) {
        const contractItems = await loadContractItemsFromFirestore(programKey);
        contractItemsData[programKey] = contractItems;
      }
      setProgramContractItems(contractItemsData);
    } catch (error) {
      console.error('Error loading all contract items:', error);
    }
  };

  // Function to get ordered items for a department
  const getOrderedItems = (department) => {
    // Unpromoted items aren't for sale, so they stay out of the comparison table.
    const deptItems = availableItems.filter(
      item => item.department === department && !isAlaCarteItemHiddenFromPurchase(item)
    );
    const savedOrder = itemOrder[department] || [];
    
    if (savedOrder.length === 0 && deptItems.length > 0) {
      // If no saved order, use default order and save it
      const defaultOrder = deptItems.map(item => ({ id: item.id, name: item.name }));
      setItemOrder(prev => ({
        ...prev,
        [department]: defaultOrder
      }));
      return deptItems;
    }
    
    // Sort items according to saved order
    const orderedItems = [];
    savedOrder.forEach(orderedItem => {
      const item = deptItems.find(deptItem => deptItem.id === orderedItem.id);
      if (item) {
        orderedItems.push(item);
      }
    });
    
    // Add any new items that weren't in the saved order
    const newItems = [];
    deptItems.forEach(item => {
      if (!orderedItems.find(orderedItem => orderedItem.id === item.id)) {
        orderedItems.push(item);
        newItems.push({ id: item.id, name: item.name });
      }
    });
    
    // If there are new items, update the itemOrder state
    if (newItems.length > 0) {
      setItemOrder(prev => ({
        ...prev,
        [department]: [...savedOrder, ...newItems]
      }));
    }
    
    return orderedItems;
  };

  // Feature selection functions
  const openFeatureSelection = async (programKey) => {
    setCurrentProgramForFeature(programKey);
    setFeatureSearchQuery('');
    setSelectedDepartment('All');
    
    try {
      // Load existing contract items from subcollection
      const existingContractItems = await loadContractItemsFromFirestore(programKey);
      setSelectedFeatures(existingContractItems);
      
      // Also update the programSelectedItems state
      setProgramSelectedItems(prev => ({
        ...prev,
        [programKey]: existingContractItems
      }));
    } catch (error) {
      console.error('Error loading contract items:', error);
      // Fallback to previously selected items
      const previouslySelected = programSelectedItems[programKey] || [];
      setSelectedFeatures(previouslySelected);
    }
    
    setShowFeatureSelectionModal(true);
  };

  const toggleFeatureSelection = (item) => {
    setSelectedFeatures(prev => {
      const isSelected = prev.find(f => f.id === item.id);
      if (isSelected) {
        return prev.filter(f => f.id !== item.id);
      } else {
        // Initialize in quantity mode by default
        return [...prev, { ...item, quantity: '1', showcase: false, isIncluded: true }];
      }
    });
  };

  const updateSelectedFeatureQuantity = (itemId, quantity) => {
    setSelectedFeatures(prev => 
      prev.map(f => {
        if (f.id === itemId) {
          // Only update quantity, don't change the mode
          return { ...f, quantity };
        }
        return f;
      })
    );
  };

  const toggleSelectedFeatureShowcase = (itemId) => {
    setSelectedFeatures(prev => 
      prev.map(f => f.id === itemId ? { ...f, showcase: !f.showcase } : f)
    );
  };

  const toggleSelectedFeatureIncluded = (itemId) => {
    setSelectedFeatures(prev => 
      prev.map(f => {
        if (f.id === itemId) {
          // If currently in quantity mode (quantity !== '0'), switch to checkbox mode (quantity = '0')
          // If currently in checkbox mode (quantity === '0'), switch to quantity mode (quantity = '1')
          const newQuantity = f.quantity === '0' ? '1' : '0';
          return { ...f, isIncluded: true, quantity: newQuantity };
        }
        return f;
      })
    );
  };

  const updateSelectedFeatureIncludedValue = (itemId, value) => {
    setSelectedFeatures(prev => 
      prev.map(f => {
        if (f.id === itemId) {
          // For checkbox mode, quantity should always be '0' when included
          // For quantity mode, quantity should be the actual quantity value
          const newQuantity = value === 'yes' ? '0' : f.quantity;
          return { ...f, isIncluded: value === 'yes', quantity: newQuantity };
        }
        return f;
      })
    );
  };

  const addSelectedFeatures = async () => {
    if (currentProgramForFeature && selectedFeatures.length > 0) {
      try {
        // Save contract items to subcollection
        await saveContractItemsToFirestore(currentProgramForFeature, selectedFeatures);
        
        // Update local state
        setProgramSelectedItems(prev => ({
          ...prev,
          [currentProgramForFeature]: selectedFeatures
        }));
        
        // Update contract items state
        setProgramContractItems(prev => ({
          ...prev,
          [currentProgramForFeature]: selectedFeatures
        }));
        
        showNotification(`Contract items saved for ${programs[currentProgramForFeature]?.name}!`, 'success');
        setShowFeatureSelectionModal(false);
        setCurrentProgramForFeature(null);
        setSelectedFeatures([]);
      } catch (error) {
        console.error('Error saving contract items:', error);
        showNotification('Error saving contract items. Please try again.', 'error');
      }
    }
  };

  // Update addFeature function to handle showcase parameter and save to Firestore
  const addFeature = async (programKey, featureName, featureDescription, showcase = false) => {
    const updatedPrograms = {
      ...programs,
      [programKey]: {
        ...programs[programKey],
        features: {
          ...programs[programKey].features,
          [featureName]: {
            quantity: featureDescription,
            showcase: showcase
          }
        }
      }
    };
    setPrograms(updatedPrograms);
    setIsValuesModified(true);
    if (showcase) {
      setIsShowcaseOrderModified(true);
    }
    
    // Auto-save to Firestore when adding a showcase feature
    if (showcase) {
      try {
        await saveProgramsToFirestore(updatedPrograms);
        showNotification(`Feature "${featureName}" added to showcase and saved!`, 'success');
        console.log('Showcase feature added and saved to Firestore');
      } catch (error) {
        console.error('Error saving showcase feature to Firestore:', error);
        showNotification('Error saving showcase feature. Please try again.', 'error');
      }
    }
  };

  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    }
    return pricePerMonth * 12;
  };

  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    }
    return pricePerMonth;
  };

  const updateBillingCycle = (cycleKey, field, value) => {
    const updatedBillingCycles = {
      ...billingCycles,
      [cycleKey]: {
        ...billingCycles[cycleKey],
        [field]: value
      }
    };
    setBillingCycles(updatedBillingCycles);
    saveBillingCyclesToFirestore(updatedBillingCycles);
  };

  // Function to set most popular program (ensures only one is most popular) and save to Firestore
  const setMostPopularProgram = async (programKey) => {
    const updatedPrograms = { ...programs };
    // Set all programs to not most popular
    Object.keys(updatedPrograms).forEach(key => {
      updatedPrograms[key].isMostPopular = false;
    });
    // Set the selected program as most popular
    updatedPrograms[programKey].isMostPopular = true;
    setPrograms(updatedPrograms);
    try {
      await saveProgramsToFirestore(updatedPrograms);
      showNotification(`"${updatedPrograms[programKey].name}" set as Most Popular and saved!`, 'success');
    } catch (error) {
      console.error('Error saving most popular program to Firestore:', error);
      showNotification('Failed to save. Please try again.', 'error');
    }
  };

  // Function to toggle showcase feature and save to Firestore
  const toggleShowcaseFeature = async (programKey, featureName) => {
    if (!programs[programKey] || !programs[programKey].features || !programs[programKey].features[featureName]) {
      console.warn('Program or feature not found:', programKey, featureName);
      return;
    }
    
    const updatedPrograms = {
      ...programs,
      [programKey]: {
        ...programs[programKey],
        features: {
          ...programs[programKey].features,
          [featureName]: {
            ...programs[programKey].features[featureName],
            showcase: !programs[programKey].features[featureName].showcase
          }
        }
      }
    };
    setPrograms(updatedPrograms);
    setIsValuesModified(true);
    setIsShowcaseOrderModified(true);
    
    // Also update editingProgram state if we're currently editing this program
    if (isEditing && editingProgram && editingProgram.key === programKey) {
      setEditingProgram(prev => ({
        ...prev,
        features: {
          ...prev.features,
          [featureName]: {
            ...prev.features[featureName],
            showcase: !prev.features[featureName].showcase
          }
        }
      }));
    }
    
    // Auto-save to Firestore when showcase feature is toggled
    try {
      await saveProgramsToFirestore(updatedPrograms);
      const action = updatedPrograms[programKey].features[featureName].showcase ? 'added to' : 'removed from';
      showNotification(`Feature "${featureName}" ${action} showcase and saved!`, 'success');
      console.log('Showcase feature toggled and saved to Firestore');
    } catch (error) {
      console.error('Error saving showcase feature to Firestore:', error);
      showNotification('Error saving showcase feature. Please try again.', 'error');
    }
  };

  // Edit program functions
  const startEditing = (programKey) => {
    setEditingProgram({ ...programs[programKey], key: programKey });
    setIsEditing(true);
  };

  const saveEdit = async () => {
    if (editingProgram) {
      const { key, ...programData } = editingProgram;
      // The input keeps whatever was typed (including a cleared field) so editing
      // feels normal; the stored value is always a whole number of months, 1-60.
      programData.minimumTerm = normalizeMinimumTerm(programData.minimumTerm);
      const updatedPrograms = {
        ...programs,
        [key]: programData
      };
      setPrograms(updatedPrograms);
      setIsValuesModified(true);
      
      // Save to Firestore
      try {
        await saveProgramsToFirestore(updatedPrograms);
        showNotification(`Program "${programData.name}" saved successfully!`, 'success');
      } catch (error) {
        console.error('Error saving program to Firestore:', error);
        showNotification('Error saving program. Please try again.', 'error');
      }
      
      setIsEditing(false);
      setEditingProgram(null);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingProgram(null);
  };

  const updateEditingProgram = (field, value) => {
    if (editingProgram) {
      setEditingProgram(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const updateEditingProgramFeature = (featureName, newName, newQuantity) => {
    if (editingProgram) {
      const newFeatures = { ...editingProgram.features };
      if (featureName !== newName) {
        delete newFeatures[featureName];
      }
      newFeatures[newName] = {
        quantity: newQuantity,
        showcase: newFeatures[newName]?.showcase || false
      };
      setEditingProgram(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const removeEditingProgramFeature = (featureName) => {
    if (editingProgram) {
      const newFeatures = { ...editingProgram.features };
      delete newFeatures[featureName];
      setEditingProgram(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const addEditingProgramFeature = (featureName, featureDescription) => {
    if (editingProgram) {
      setEditingProgram(prev => ({
        ...prev,
        features: {
          ...prev.features,
          [featureName]: {
            quantity: featureDescription,
            showcase: false
          }
        }
      }));
    }
  };

  // Showcase features (freeform text rows) - add, remove, update, toggle star
  const addShowcaseFeatureRow = () => {
    if (editingProgram) {
      setEditingProgram(prev => ({
        ...prev,
        showcaseFeatures: [...(prev.showcaseFeatures || []), { text: '', showcase: true, description: '' }]
      }));
    }
  };

  const updateShowcaseFeatureRow = (index, text) => {
    if (editingProgram) {
      setEditingProgram(prev => {
        const arr = [...(prev.showcaseFeatures || [])];
        const item = arr[index];
        arr[index] = typeof item === 'string' ? { text, showcase: true, description: '' } : { ...item, text };
        return { ...prev, showcaseFeatures: arr };
      });
    }
  };

  const updateShowcaseFeatureDescription = (index, description) => {
    if (editingProgram) {
      setEditingProgram(prev => {
        const arr = [...(prev.showcaseFeatures || [])];
        const item = arr[index];
        arr[index] = typeof item === 'string' ? { text: item, showcase: true, description } : { ...item, description };
        return { ...prev, showcaseFeatures: arr };
      });
    }
  };

  const toggleShowcaseFeatureRow = (index) => {
    if (editingProgram) {
      setEditingProgram(prev => {
        const arr = [...(prev.showcaseFeatures || [])];
        const item = arr[index];
        arr[index] = typeof item === 'string' ? { text: item, showcase: false } : { ...item, showcase: !item.showcase };
        return { ...prev, showcaseFeatures: arr };
      });
    }
  };

  const removeShowcaseFeatureRow = (index) => {
    if (editingProgram) {
      setEditingProgram(prev => ({
        ...prev,
        showcaseFeatures: (prev.showcaseFeatures || []).filter((_, i) => i !== index)
      }));
    }
  };

  // Array order is the order the cards render in on the program pages, so
  // reordering here is just swapping neighbours. `direction` is -1 (up) or 1 (down).
  const moveShowcaseFeatureRow = (index, direction) => {
    if (!editingProgram) return;
    setEditingProgram(prev => {
      const arr = [...(prev.showcaseFeatures || [])];
      const target = index + direction;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...prev, showcaseFeatures: arr };
    });
  };

  // Open feature selection for editing program
  const openFeatureSelectionForEdit = () => {
    setCurrentProgramForFeature(editingProgram.key);
    // Load previously selected items for this program
    const previouslySelected = programSelectedItems[editingProgram.key] || [];
    setSelectedFeatures(previouslySelected);
    setFeatureSearchQuery('');
    setSelectedDepartment('All');
    setShowFeatureSelectionModal(true);
  };

  // Add new program functions
  const addNewProgram = () => {
    if (newProgram.name && newProgram.description && newProgram.pricePerMonth) {
      const programKey = newProgram.name.replace(/\s+/g, '-').toLowerCase();
      
      const updatedPrograms = { ...programs };
      updatedPrograms[programKey] = {
        ...newProgram,
        minimumTerm: normalizeMinimumTerm(newProgram.minimumTerm),
        isMostPopular: false,
      };
      
      setPrograms(updatedPrograms);
      setIsValuesModified(true);
      
      setShowAddModal(false);
      setNewProgram({
        name: '',
        description: '',
        pricePerMonth: 0,
        isMostPopular: false,
        minimumTerm: 12,
        features: {}
      });
    }
  };

  // Feature management functions
  const removeFeature = (programKey, featureName) => {
    const newFeatures = { ...programs[programKey].features };
    const wasShowcase = newFeatures[featureName]?.showcase || false;
    delete newFeatures[featureName];
    const updatedPrograms = {
      ...programs,
      [programKey]: {
        ...programs[programKey],
        features: newFeatures
      }
    };
    setPrograms(updatedPrograms);
    setIsValuesModified(true);
    if (wasShowcase) {
      setIsShowcaseOrderModified(true);
    }
  };

  // Add Feature Modal Component
  const AddFeatureModal = ({ programKey, onClose }) => {
    const [featureName, setFeatureName] = useState('');
    const [featureDescription, setFeatureDescription] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (featureName && featureDescription) {
        await addFeature(programKey, featureName, featureDescription);
        setFeatureName('');
        setFeatureDescription('');
        onClose();
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
          <div className="bg-blue-600 rounded-t-lg p-4">
            <h3 className="text-lg font-semibold text-white">Add New Feature</h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Feature Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={featureName}
                  onChange={(e) => setFeatureName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Feature Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  value={featureDescription}
                  onChange={(e) => setFeatureDescription(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Feature
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Add Program Modal Component
  const AddProgramModal = () => {
    const [showFeatureForm, setShowFeatureForm] = useState(false);
    const [tempFeatureName, setTempFeatureName] = useState('');
    const [tempFeatureDescription, setTempFeatureDescription] = useState('');
    const [tempFeatureShowcase, setTempFeatureShowcase] = useState(false);

    const addTempFeature = () => {
      if (tempFeatureName && tempFeatureDescription) {
        setNewProgram(prev => ({
          ...prev,
          features: {
            ...prev.features,
            [tempFeatureName]: {
              quantity: tempFeatureDescription,
              showcase: tempFeatureShowcase
            }
          }
        }));
        setTempFeatureName('');
        setTempFeatureDescription('');
        setTempFeatureShowcase(false);
        setShowFeatureForm(false);
      }
    };

    const removeTempFeature = (featureName) => {
      const newFeatures = { ...newProgram.features };
      delete newFeatures[featureName];
      setNewProgram(prev => ({
        ...prev,
        features: newFeatures
      }));
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="bg-blue-600 rounded-t-lg p-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Add New Program</h3>
            <button
              onClick={() => setShowAddModal(false)}
              className="text-white hover:text-gray-200"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Program Name *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProgram.name}
                  onChange={(e) => setNewProgram(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Price Per Month *
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProgram.pricePerMonth}
                  onChange={(e) => setNewProgram(prev => ({ ...prev, pricePerMonth: parseInt(e.target.value) || 0 }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Minimum Term (months) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProgram.minimumTerm ?? ''}
                  onChange={(e) => setNewProgram(prev => ({ ...prev, minimumTerm: e.target.value }))}
                  onBlur={(e) => setNewProgram(prev => ({ ...prev, minimumTerm: normalizeMinimumTerm(e.target.value) }))}
                  required
                />
              </div>

            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                value={newProgram.description}
                onChange={(e) => setNewProgram(prev => ({ ...prev, description: e.target.value }))}
                required
              />
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Features</h4>
                <button
                  type="button"
                  onClick={() => setShowFeatureForm(true)}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  <FaPlus className="inline mr-1" />
                  Add Feature
                </button>
              </div>
              
                             {Object.entries(newProgram.features).map(([name, featureData]) => (
                 <div key={name} className="flex justify-between items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-md mb-2">
                   <div className="flex-1">
                     <div className="flex items-center gap-2">
                       <p className="font-medium text-gray-900 dark:text-white">{name}</p>
                       {featureData.showcase && (
                         <FaStar className="text-yellow-500 text-xs" title="Showcase Feature" />
                       )}
                     </div>
                     <p className="text-xs text-gray-600 dark:text-gray-400">Quantity: {featureData.quantity}</p>
                   </div>
                   <button
                     onClick={() => removeTempFeature(name)}
                     className="text-red-500 hover:text-red-700 ml-2"
                   >
                     <FaTrash className="h-4 w-4" />
                   </button>
                 </div>
               ))}

                             {showFeatureForm && (
                  <div className="mt-4 p-4 border border-gray-300 dark:border-gray-600 rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Feature name"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={tempFeatureName}
                        onChange={(e) => setTempFeatureName(e.target.value)}
                      />
                      <div className="flex items-center space-x-2">
                        {tempFeatureDescription === '0' ? (
                          <select
                            value="no"
                            onChange={(e) => {
                              const newQuantity = e.target.value === 'yes' ? '1' : '0';
                              setTempFeatureDescription(newQuantity);
                            }}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder="Unit quantity (e.g., 1, 3, 12)"
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={tempFeatureDescription}
                            onChange={(e) => setTempFeatureDescription(e.target.value)}
                          />
                        )}
                                                 <button
                           onClick={() => {
                             const newQuantity = tempFeatureDescription === '0' ? '1' : '0';
                             setTempFeatureDescription(newQuantity);
                           }}
                           className={`px-2 py-1 text-xs rounded border flex items-center justify-center ${
                             tempFeatureDescription === '0'
                               ? 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                               : 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                           }`}
                           title={tempFeatureDescription === '0' ? 'Switch to quantity mode' : 'Switch to checkbox mode'}
                         >
                           {tempFeatureDescription === '0' ? '✓' : '☐'}
                         </button>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center space-x-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-gray-600 text-yellow-500 focus:ring-yellow-500"
                          checked={tempFeatureShowcase}
                          onChange={(e) => setTempFeatureShowcase(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Showcase Feature</span>
                      </label>
                    </div>
                    <div className="mt-3 flex space-x-2">
                      <button
                        type="button"
                        onClick={addTempFeature}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFeatureForm(false)}
                        className="px-3 py-1 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addNewProgram}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create Program
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Billing Cycles Modal Component
  const BillingCyclesModal = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempBillingCycles, setTempBillingCycles] = useState({
      monthly: { name: 'Monthly', discount: 0, months: 1 },
      annual: { name: 'Annual', discount: 20, months: 12 }
    });
    
    if (!showBillingModal) return null;

    const handleSave = async () => {
      try {
        await saveBillingCyclesToFirestore(tempBillingCycles);
        setBillingCycles(tempBillingCycles);
        setIsEditing(false);
        setShowBillingModal(false);
        // Show success message (you can add a toast notification here)
        console.log('Billing cycles updated successfully');
      } catch (error) {
        console.error('Failed to save billing cycles:', error);
        // Show error message to user (you can add error handling UI here)
        alert('Failed to save billing cycles. Please try again.');
      }
    };

    const handleCancel = () => {
      setTempBillingCycles({
        monthly: { name: 'Monthly', discount: 0, months: 1 },
        annual: { name: 'Annual', discount: 20, months: 12 }
      });
      setIsEditing(false);
    };

    const updateTempBillingCycle = (cycleKey, field, value) => {
      setTempBillingCycles(prev => ({
        ...prev,
        [cycleKey]: {
          ...prev[cycleKey],
          [field]: value
        }
      }));
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="bg-purple-600 rounded-t-lg p-4">
            <div className="flex justify-between items-center">
              <div className="w-8"></div>
              <h3 className="text-lg font-semibold text-white text-center flex-1">Edit Billing Cycles</h3>
              <button
                onClick={() => setShowBillingModal(false)}
                className="text-white hover:text-gray-200 w-8"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Billing Cycle Configuration</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Configure the annual discount percentage. Monthly billing has no discount.
                </p>
              </div>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <FaEdit className="mr-2" />
                    Edit Annual Discount
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                    >
                      <FaSave className="mr-2" />
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                    >
                      <FaTimes className="mr-2" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(tempBillingCycles).map(([cycleKey, cycle]) => (
                <div key={cycleKey} className="p-6 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center text-lg">{cycle.name}</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Display Name
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        value={cycle.name}
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          isEditing && cycleKey === 'annual'
                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white' 
                            : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                        value={cycle.discount}
                        onChange={(e) => updateTempBillingCycle(cycleKey, 'discount', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        disabled={!isEditing || cycleKey !== 'annual'}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {cycleKey === 'annual' ? 'Set annual discount percentage' : 'Monthly billing has no discount'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Billing Period (months)
                      </label>
                      <input
                        type="number"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        value={cycle.months}
                        disabled
                        readOnly
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Billing period is fixed
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowBillingModal(false)}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Hourly Options Modal Component
  const HourlyOptionsModal = () => {
    const [tempHourlyOptions, setTempHourlyOptions] = useState(hourlyOptions);
    
    if (!showHourlyOptionsModal) return null;

    const handleUpdatePrice = (optionId, pricePerMonth) => {
      setTempHourlyOptions(prev => 
        prev.map(option => 
          option.id === optionId 
            ? { ...option, pricePerMonth: parseInt(pricePerMonth) || 0 }
            : option
        )
      );
    };

    const getDiscountPercentage = (option) => {
      if (option.hours === 'unlimited') {
        return '0%'; // No discount for hourly rate
      }
      // Calculate discount based on hourly rate vs package rate
      const hourlyRate = 150; // Base hourly rate
      const packageRatePerHour = option.hours > 0 ? option.pricePerMonth / option.hours : 0;
      const discount = hourlyRate > 0 && packageRatePerHour > 0 
        ? Math.round(((hourlyRate - packageRatePerHour) / hourlyRate) * 100)
        : 0;
      return `${Math.max(0, discount)}%`;
    };

    const handleSave = async () => {
      try {
        await saveHourlyOptionsToFirestore(tempHourlyOptions);
        setHourlyOptions(tempHourlyOptions);
        setShowHourlyOptionsModal(false);
        showNotification('Hourly options updated successfully!', 'success');
        console.log('Hourly options updated successfully');
      } catch (error) {
        console.error('Failed to save hourly options:', error);
        showNotification('Failed to save hourly options. Please try again.', 'error');
      }
    };

    const handleCancel = () => {
      setTempHourlyOptions(hourlyOptions);
      setShowHourlyOptionsModal(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="bg-orange-600 rounded-t-lg p-4">
            <div className="flex justify-between items-center">
              <div className="w-8"></div>
              <h3 className="text-lg font-semibold text-white text-center flex-1">Edit Hourly Options</h3>
              <button
                onClick={handleCancel}
                className="text-white hover:text-gray-200 w-8"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Hourly Options Configuration</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure pricing for different hourly packages. Edit the monthly price and see the calculated price per hour below.
              </p>
            </div>

            {/* Hourly Options */}
            <div className="mb-6">
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Hourly Package Options</h5>
              <div className="space-y-4">
                {tempHourlyOptions.map((option) => (
                  <div key={option.id} className="p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">⏰</div>
                        <div>
                          <h6 className="font-semibold text-gray-900 dark:text-white text-lg">
                            {option.hours === 'unlimited' ? 'Hourly Rate' : `${option.hours} Hours/Month`}
                          </h6>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {option.hours === 'unlimited' 
                              ? 'Pay as you go - charged for each hour used' 
                              : `Monthly package with ${option.hours} hours included`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {option.hours === 'unlimited' ? 'Hourly Rate ($)' : 'Monthly Price ($)'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                          value={option.pricePerMonth}
                          onChange={(e) => handleUpdatePrice(option.id, e.target.value)}
                          placeholder={option.hours === 'unlimited' ? "Enter hourly rate" : "Enter monthly price"}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {option.hours === 'unlimited' ? 'Hourly Rate' : 'Discount'}
                        </label>
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white">
                          {option.hours === 'unlimited' ? `$${option.pricePerMonth}/hour` : getDiscountPercentage(option)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center"
              >
                <FaSave className="mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update billing cycle state when billing cycles are updated
  useEffect(() => {
    // Ensure billing cycle is valid after billing cycles are updated
    if (billingCycles && Object.keys(billingCycles).length > 0) {
      const currentCycle = billingCycles[billingCycle];
      if (!currentCycle) {
        // If current billing cycle doesn't exist, set to first available
        setBillingCycle(Object.keys(billingCycles)[0]);
      }
    }
  }, [billingCycles]);

  // Retired programs (Lite, Plus, Pro) stay in Firestore for current clients but aren't shown here.
  const visibleProgramEntries = Object.entries(programs || {}).filter(([key, program]) => !isRetiredProgram(key, program));

  return (
    <div className="min-h-screen py-8 px-4 md:px-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/crm/templates')}
            className="inline-flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FaArrowLeft className="h-4 w-4" />
          </button>

          {/* Page Title */}
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Programs Template
          </h1>

          <button
            onClick={() => setShowBillingModal(true)}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center"
          >
            <FaCog className="mr-2" />
            Billing Cycles
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading programs and billing cycles...
            </div>
          </div>
        )}

        {/* No offered programs: the page is the custom program builder (same as /crm/programs-template/custom).
            Keyed on billing cycles so it picks up edits made in the Billing Cycles modal. */}
        {!isLoading && visibleProgramEntries.length === 0 && (
          <ProgramDetail
            key={JSON.stringify(billingCycles)}
            embedded={true}
            selectedProgramProp={CUSTOM_PROGRAM}
            selectedTierProp={CUSTOM_TIER}
            hideActionButton={true}
            hideHeader={true}
          />
        )}

        {/* Content - Only show when not loading */}
        {!isLoading && visibleProgramEntries.length > 0 && (
          <>
            {/* Billing Toggle */}
            <div className="flex justify-center items-center gap-3 mb-8">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-1 shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex">
                  {Object.entries(billingCycles).map(([cycleKey, cycle]) => (
                    <button
                      key={cycleKey}
                      onClick={() => setBillingCycle(cycleKey)}
                      className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                        billingCycle === cycleKey
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {cycle.name}
                      {cycle.discount > 0 && (
                        <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                          Save {cycle.discount}%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Program Cards */}
            <div className="grid grid-cols-3 gap-8 mb-12">
              {visibleProgramEntries.map(([programKey, program]) => {
                const validatedProgram = validateProgram(program);
                return (
                  <div
                    key={programKey}
                    className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-200"
                  >

                  
                  {/* Edit Controls Overlay */}
                  <div className="absolute top-2 right-2 z-30">
                    <button
                      onClick={() => startEditing(programKey)}
                      className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                      title="Edit Program"
                    >
                      <FaEdit className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    <div className="text-center mb-4">
                      {/* Star for Most Popular */}
                      <div className="absolute top-4 left-4 z-20">
                        <button
                          onClick={() => setMostPopularProgram(programKey)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            validatedProgram.isMostPopular
                              ? 'bg-yellow-500 text-white shadow-lg hover:bg-yellow-600'
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-500'
                          }`}
                          title={validatedProgram.isMostPopular ? 'Most Popular Program' : 'Click to make Most Popular'}
                        >
                          <FaStar className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{validatedProgram.name}</h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{validatedProgram.description}</p>
                    </div>

                    <div className="text-center mb-6">
                      <div className="flex items-baseline justify-center">
                        <span className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">$</span>
                        <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white ml-1">
                          {getMonthlyPrice(validatedProgram.pricePerMonth)}
                        </span>
                        <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                      </div>

                      {billingCycle === 'annual' && billingCycles?.annual && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          ${getAnnualPrice(validatedProgram.pricePerMonth)} billed {billingCycles.annual.name.toLowerCase()}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Minimum term: {validatedProgram.minimumTerm} {validatedProgram.minimumTerm === 1 ? 'month' : 'months'}
                      </p>
                    </div>



                    {/* Showcase Features - from freeform text rows */}
                    {(() => {
                      const items = (validatedProgram.showcaseFeatures || [])
                        .map(item => typeof item === 'string' ? { text: item, showcase: true } : item)
                        .filter(item => item.showcase && item.text?.trim());
                      return items.length > 0 ? (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-yellow-600 dark:text-yellow-400 flex items-center gap-2 mb-2">
                            <FaStar className="text-yellow-500" />
                            Showcase Features
                          </h4>
                          <div className="space-y-2">
                            {items.map((item, index) => (
                              <div key={index} className="flex items-start bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-md">
                                <FaStar className="text-yellow-500 mt-1 mr-2 flex-shrink-0 text-xs" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{item.text}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 text-center">
                            💡 Showcase features appear on program cards. Edit in the popup to add/remove.
                          </p>
                        </div>
                      ) : null;
                    })()}

                    {/* Contract Items Section */}
                    {programContractItems[programKey] && programContractItems[programKey].length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2 mb-2">
                          <FaCog className="text-blue-500" />
                          Contract Items
                        </h4>
                        <div className="space-y-1">
                          {programContractItems[programKey].slice(0, 3).map((item, index) => (
                            <div key={item.id || index} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md">
                              <div className="flex items-center space-x-2">
                                <div className="text-lg">{item.icon || '📦'}</div>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</span>
                              </div>
                              <div className="flex items-center">
                                {item.quantity === '0' ? (
                                  <FaCheck className="text-green-500 text-xs" />
                                ) : (
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                                    {item.quantity}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {programContractItems[programKey].length > 3 && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 text-center">
                              +{programContractItems[programKey].length - 3} more items
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Contract Items Button */}
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={() => openFeatureSelection(programKey)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                      >
                        <FaPlus className="mr-2" />
                        Contract Items
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
            </div>

            {/* Program Features Comparison Table */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Items Comparison</h3>
                    {isOrderModified && (
                      <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                        Order Modified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    {!isOrderModified ? (
                      <button
                        onClick={() => setIsOrderModified(true)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
                      >
                        <FaEdit className="mr-1" />
                        Edit Order
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                          Order Modified
                        </span>
                        <button
                          onClick={saveItemOrder}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center"
                        >
                          <FaSave className="mr-1" />
                          Save Order
                        </button>
                      </div>
                    )}
                    
                    {!isValuesModified ? (
                      <button
                        onClick={() => setIsValuesModified(true)}
                        className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 flex items-center"
                      >
                        <FaEdit className="mr-1" />
                        Edit Values
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                          Values Modified
                        </span>
                        <button
                          onClick={saveQuantities}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center"
                        >
                          <FaSave className="mr-1" />
                          Save Values
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                  💡 <strong>How to use:</strong> Use "Edit Order" to rearrange items within departments. Use "Edit Values" to modify quantities for each program. Use "Quantity Yes/No" button to switch between quantity (numbers) and Yes/No modes. Program cards only show showcase features.
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">
                        <div className="flex items-center space-x-2">
                          <span>Items</span>
                          {isOrderModified && (
                            <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                              Order Modified
                            </span>
                          )}
                          {isValuesModified && (
                            <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                              Values Modified
                            </span>
                          )}
                        </div>
                      </th>
                      {visibleProgramEntries.map(([programKey]) => {
                        const validatedProgram = validateProgram(programs[programKey]);
                        return (
                          <th key={programKey} className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {validatedProgram.name}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(() => {
                      const rows = [];
                      

                      
                      // Add department sections
                      const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
                      departments.forEach(dept => {
                        const deptItems = getOrderedItems(dept);
                        if (deptItems.length > 0) {
                          // Add department header row
                          rows.push(
                            <tr key={`dept-${dept}`} className="bg-gray-50 dark:bg-gray-700">
                              <td colSpan={visibleProgramEntries.length + 1} className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {dept} Department
                              </td>
                            </tr>
                          );
                          
                          // Add department items
                          deptItems.forEach((item, itemIndex) => {
                            const isFirst = itemIndex === 0;
                            const isLast = itemIndex === deptItems.length - 1;
                            
                            rows.push(
                              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-3">
                                    {isOrderModified && (
                                      <div className="flex items-center space-x-2">
                                        <button
                                          onClick={() => moveItem(dept, item.id, 'up')}
                                          disabled={isFirst}
                                          className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                                            isFirst 
                                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                              : 'bg-blue-600 text-white hover:bg-blue-700'
                                          }`}
                                          title="Move Up"
                                        >
                                          <FaArrowUp className="h-3 w-3" />
                                        </button>
                                        <button
                                          onClick={() => moveItem(dept, item.id, 'down')}
                                          disabled={isLast}
                                          className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                                            isLast 
                                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                              : 'bg-blue-600 text-white hover:bg-blue-700'
                                          }`}
                                          title="Move Down"
                                        >
                                          <FaArrowDown className="h-3 w-3" />
                                        </button>
                                      </div>
                                    )}
                                    <div className="text-2xl">{item.icon || '📦'}</div>
                                    <div>
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">{item.name}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                                      <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getDepartmentColor(item.department)}`}>
                                        {item.department}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                {visibleProgramEntries.map(([programKey, program]) => {
                                  const validatedProgram = validateProgram(program);
                                  const currentQuantity = validatedProgram.features[item.name]?.quantity || '0';
                                  const isCheckbox = currentQuantity === '0';
                                  const isIncluded = validatedProgram.features[item.name] !== undefined;
                                  
                                  return (
                                    <td key={programKey} className="px-6 py-4 text-center">
                                      <div className="flex items-center justify-center space-x-2">
                                        {isValuesModified ? (
                                          <>
                                            {/* Checkbox for inclusion */}
                                            <input
                                              type="checkbox"
                                              checked={isIncluded}
                                              onChange={async (e) => {
                                                if (e.target.checked) {
                                                  // Add item with default quantity '1' (quantity mode)
                                                  const isShowcase = false;
                                                  await addFeature(programKey, item.name, '1', isShowcase);
                                                } else {
                                                  // Remove item
                                                  removeFeature(programKey, item.name);
                                                }
                                              }}
                                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                              title="Include this item in the program"
                                            />
                                            
                                            {/* Quantity input or checkbox toggle */}
                                            {isIncluded && (
                                              <div className="flex items-center space-x-1">
                                                {isCheckbox ? (
                                                  <select
                                                    value={currentQuantity === '0' ? 'no' : 'yes'}
                                                    onChange={async (e) => {
                                                      const newValue = e.target.value === 'yes' ? '0' : '1';
                                                      const isShowcase = validatedProgram.features[item.name]?.showcase || false;
                                                      await addFeature(programKey, item.name, newValue, isShowcase);
                                                    }}
                                                    className="w-16 px-1 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                  >
                                                    <option value="no">No</option>
                                                    <option value="yes">Yes</option>
                                                  </select>
                                                ) : (
                                                  <input
                                                    type="number"
                                                    min="1"
                                                    className="w-16 px-1 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    value={currentQuantity === '0' ? '1' : currentQuantity}
                                                    onChange={async (e) => {
                                                      const newValue = e.target.value;
                                                      if (newValue && newValue !== '0') {
                                                        const isShowcase = validatedProgram.features[item.name]?.showcase || false;
                                                        await addFeature(programKey, item.name, newValue, isShowcase);
                                                      }
                                                    }}
                                                    placeholder="1"
                                                  />
                                                )}
                                                
                                                {/* Quantity Yes/No toggle button */}
                                                <button
                                                  onClick={async () => {
                                                    const newMode = isCheckbox ? '1' : '0';
                                                    const isShowcase = validatedProgram.features[item.name]?.showcase || false;
                                                    await addFeature(programKey, item.name, newMode, isShowcase);
                                                  }}
                                                  className={`px-2 py-1 text-xs rounded border flex items-center justify-center ${
                                                    isCheckbox
                                                      ? 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                                                      : 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                                                  }`}
                                                  title={isCheckbox ? 'Switch to quantity mode' : 'Switch to Yes/No mode'}
                                                >
                                                  {isCheckbox ? '☐' : '✓'}
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          /* Display mode - show visual indicators */
                                          <>
                                            {/* Inclusion indicator */}
                                            {isIncluded ? (
                                              <div className="flex items-center justify-center">
                                                {isCheckbox ? (
                                                  <FaCheck className="text-green-500 text-sm" />
                                                ) : (
                                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {currentQuantity}
                                                  </span>
                                                )}
                                              </div>
                                            ) : (
                                              <FaTimes className="text-red-500 text-sm" />
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          });
                        }
                      });
                      
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showAddModal && <AddProgramModal />}
      {showBillingModal && <BillingCyclesModal />}
      {showHourlyOptionsModal && <HourlyOptionsModal />}
      
      {/* Edit Program Modal - Overlay for editing program details */}
      {isEditing && editingProgram && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-blue-600 rounded-t-lg p-4">
              <h3 className="text-lg font-semibold text-white text-center">Editing: {editingProgram.name}</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Basic Program Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Program Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editingProgram.name}
                      onChange={(e) => updateEditingProgram('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Price Per Month
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editingProgram.pricePerMonth}
                      onChange={(e) => updateEditingProgram('pricePerMonth', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Minimum Term (months)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editingProgram.minimumTerm ?? ''}
                      onChange={(e) => updateEditingProgram('minimumTerm', e.target.value)}
                      onBlur={(e) => updateEditingProgram('minimumTerm', normalizeMinimumTerm(e.target.value))}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Minimum contract term for this program (1-60 months)
                    </p>
                  </div>

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    value={editingProgram.description}
                    onChange={(e) => updateEditingProgram('description', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    💡 <strong>Note:</strong> Program items are managed in the Items Comparison table below.
                  </p>
                </div>

                {/* Showcase Features Section - freeform text rows for program cards */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <FaStar className="text-yellow-500" />
                    Showcase Features
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Click the star (⭐) button to toggle showcase status. Showcase features appear on program cards in
                    the order below — use the arrows to reorder them.
                  </p>
                  <div className="space-y-3">
                    {(editingProgram.showcaseFeatures || []).map((item, index) => {
                      const text = typeof item === 'string' ? item : item.text;
                      const description = typeof item === 'string' ? '' : (item.description || '');
                      const showcase = typeof item === 'string' ? true : item.showcase;
                      const rowCount = (editingProgram.showcaseFeatures || []).length;
                      return (
                        <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="flex flex-col flex-shrink-0">
                              <button
                                onClick={() => moveShowcaseFeatureRow(index, -1)}
                                disabled={index === 0}
                                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-500 dark:text-gray-400 dark:hover:text-white"
                                title="Move up"
                              >
                                <FaArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveShowcaseFeatureRow(index, 1)}
                                disabled={index === rowCount - 1}
                                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-500 dark:text-gray-400 dark:hover:text-white"
                                title="Move down"
                              >
                                <FaArrowDown className="h-3 w-3" />
                              </button>
                            </span>
                            <input
                              type="text"
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              value={text}
                              onChange={(e) => updateShowcaseFeatureRow(index, e.target.value)}
                              placeholder="Feature title..."
                            />
                            <button
                              onClick={() => toggleShowcaseFeatureRow(index)}
                              className={`px-2 py-1.5 rounded text-sm flex items-center justify-center flex-shrink-0 ${
                                showcase
                                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                  : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                              }`}
                              title={showcase ? 'Remove from showcase (hide on card)' : 'Add to showcase (show on card)'}
                            >
                              <FaStar className="text-xs" />
                            </button>
                            <button
                              onClick={() => removeShowcaseFeatureRow(index)}
                              className="text-red-500 hover:text-red-700 flex-shrink-0 p-2"
                              title="Remove row"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            value={description}
                            onChange={(e) => updateShowcaseFeatureDescription(index, e.target.value)}
                            placeholder="Description (shows in popup when clicked on programs page)"
                            rows="2"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={addShowcaseFeatureRow}
                    className="mt-2 flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-gray-400 dark:border-gray-500 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <FaPlus className="h-3 w-3" />
                    Add row
                  </button>
                </div>
              </div>

              <div className="mt-6 flex justify-between items-center">
                {/* No delete: programs hold clients' signed prices and deliverables. Retire instead. */}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Programs can't be deleted, because current clients' prices and deliverables depend on them.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                  >
                    <FaSave className="mr-1" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Selection Modal */}
      {showFeatureSelectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                         <div className="bg-blue-600 rounded-t-lg p-4">
               <div className="flex justify-between items-center">
                 <div className="w-8"></div>
                 <h3 className="text-lg font-semibold text-white text-center flex-1">
                   {isEditing && editingProgram 
                     ? `Select Items from Data Collection for "${editingProgram.name}"`
                     : `Select Items from Data Collection for ${currentProgramForFeature ? programs[currentProgramForFeature]?.name : 'Program'}`
                   }
                 </h3>
                 <button
                   onClick={() => setShowFeatureSelectionModal(false)}
                   className="text-white hover:text-gray-200 w-8"
                 >
                   <FaTimes className="h-5 w-5" />
                 </button>
               </div>
             </div>
            
            <div className="p-6">
              {/* Search and Filter */}
              <div className="mb-6 space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search items from data collection..."
                        value={featureSearchQuery}
                        onChange={(e) => setFeatureSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="w-48">
                    <select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="All">All Departments</option>
                      <option value="General">General</option>
                      <option value="Sales">Sales</option>
                      <option value="Operations">Operations</option>
                      <option value="Finance">Finance</option>
                      <option value="Technology">Technology</option>
                      <option value="Marketing">Marketing</option>
                      <option value="HR">HR</option>
                    </select>
                  </div>
                </div>
              </div>

                             {/* Available Items Grid */}
               <div className="mb-4">
                 <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Select Items to Manage for This Program</h4>
               </div>
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                 {filteredAvailableItems.map(item => {
                   const isSelected = selectedFeatures.find(f => f.id === item.id);
                   return (
                     <div
                       key={item.id}
                       className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                         isSelected 
                           ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                           : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                       }`}
                       onClick={() => toggleFeatureSelection(item)}
                     >
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">{item.icon || '📦'}</div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                            {item.description}
                          </p>
                          <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getDepartmentColor(item.department || 'General')}`}>
                            {item.department || 'General'}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {isSelected && (
                            <FaCheck className="text-blue-500 text-lg" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected Features */}
              {selectedFeatures.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Selected Items to Add</h4>
                  <div className="space-y-3">
                    {selectedFeatures.map(feature => (
                      <div key={feature.id} className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                        <div className="text-xl">{feature.icon || '📦'}</div>
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 dark:text-white">{feature.name}</h5>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{feature.description}</p>
                        </div>
                        <div className="flex items-center space-x-3">
                                                     <div>
                             <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Quantity</label>
                             <div className="flex items-center space-x-2">
                               {feature.quantity === '0' ? (
                                 <select
                                   value="yes"
                                   onChange={(e) => updateSelectedFeatureIncludedValue(feature.id, e.target.value)}
                                   className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                 >
                                   <option value="yes">Yes</option>
                                   <option value="no">No</option>
                                 </select>
                               ) : (
                                 <input
                                   type="text"
                                   value={feature.quantity}
                                   onChange={(e) => updateSelectedFeatureQuantity(feature.id, e.target.value)}
                                   className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                   placeholder="1"
                                 />
                               )}
                               <button
                                 onClick={() => toggleSelectedFeatureIncluded(feature.id)}
                                 className={`px-2 py-1 text-xs rounded border flex items-center justify-center ${
                                   feature.quantity === '0'
                                     ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                                     : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500'
                                 }`}
                                 title={feature.quantity === '0' ? 'Switch to quantity mode' : 'Switch to checkbox mode'}
                               >
                                 {feature.quantity === '0' ? '✓' : '☐'}
                               </button>
                             </div>
                           </div>
                          <div>
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={feature.showcase}
                                onChange={() => toggleSelectedFeatureShowcase(feature.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-yellow-500 focus:ring-yellow-500"
                              />
                              <span className="text-xs text-gray-600 dark:text-gray-400">Showcase</span>
                            </label>
                          </div>
                          <button
                            onClick={() => toggleFeatureSelection(feature)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTimes className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowFeatureSelectionModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                                 <button
                   onClick={addSelectedFeatures}
                   disabled={selectedFeatures.length === 0}
                   className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                 >
                   {isEditing && editingProgram 
                     ? `Save ${selectedFeatures.length} Item${selectedFeatures.length !== 1 ? 's' : ''} for "${editingProgram.name}"`
                     : `Save ${selectedFeatures.length} Item${selectedFeatures.length !== 1 ? 's' : ''} for ${currentProgramForFeature ? programs[currentProgramForFeature]?.name : 'Program'}`
                   }
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            <span>{notification.message}</span>
                <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
                >
              <FaTimes className="h-4 w-4" />
                </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProgramsTemplate;
